/**
 * Navigation Routes - Enum-based routing system for type safety
 *
 * This file provides type-safe navigation routes and route builders.
 * All navigation should use these enums to prevent 404 errors.
 */

// Static routes that don't require parameters
export enum NavigationRoutes {
    // Authentication routes
    LOGIN = '/auth/login',
    SIGNUP = '/auth/signup',
    FORGOT_PASSWORD = '/auth/forgot-password',
    RESET_PASSWORD = '/auth/reset-password',
    VERIFY_EMAIL = '/auth/verify-email',
    COMPLETE = '/auth/complete',
    SETUP = '/auth/setup',
    SIGNIN = '/auth/signin',
    INVITE = '/auth/invite',
    MAGIC_LINK_SENT = '/auth/magic-link-sent',
    MAGIC_LINK_EXPIRED = '/auth/magic-link-expired',

    // Core app routes
    DASHBOARD = '/dashboard',
    PROFILE = '/profile',
    NOTIFICATION_SETTINGS = '/notification-settings',

    // HR Intake route
    INTAKE = '/intake',

    // Unified Upload routes (main pages)
    UPLOAD = '/upload',
    UPLOAD_HRIS = '/upload/hris-upload',
    UPLOAD_PERFORMANCE = '/upload/performance-upload',
    UPLOAD_DOCUMENT = '/upload/document-upload',

    // Team routes
    TEAM = '/team',

    // Employee specific routes
    LEARN = '/learn',
    CAREER = '/career',
    BRAG = '/brag',

    // HR and shared routes
    PEOPLE = '/people',
    CONNECT = '/connect',
    DOCUMENTATION = '/documentation',
    ALERTS = '/alerts',
    SURVEYS = '/surveys',
    KUDOS = '/kudos',
    REPORTS = '/reports',
    SUPPORT = '/support',

    // Legal routes
    TERMS = '/terms',
    PRIVACY = '/privacy',

    // Error routes
    FORBIDDEN = '/403',
    NOT_FOUND = '/404',
    ERROR = '/error',
    INTAKE_SURVEY_EXPIRED = '/intake-survey/survey-expired',
}

// Route builders for dynamic routes with parameters
export class RouteBuilders {
    // Intake survey route - REST compliant with step parameter
    static intakeSurvey(token: string, step?: string): string {
        const basePath = `/intake-survey/${token}`
        return step ? `${basePath}?step=${step}` : basePath
    }

    // Manager intake survey route - authenticated route with step parameter
    static managerIntakeSurvey(step?: string): string {
        const basePath = '/intake'
        return step ? `${basePath}?step=${step}` : basePath
    }

    // Upload detail route - REST compliant
    static uploadDetails(jobId: string, tab?: string): string {
        const basePath = `/upload/${jobId}`
        return tab ? `${basePath}?tab=${tab}` : basePath
    }

    // Team member detail route
    static teamMemberDetail(employeeId: string): string {
        return `/team/${employeeId}`
    }

    // Upload routes with tabs
    static uploadHrisWithTab(tab: string): string {
        return `/upload/hris-upload?tab=${tab}`
    }

    static uploadPerformanceWithTab(tab: string): string {
        return `/upload/performance-upload?tab=${tab}`
    }

    static uploadDocumentWithTab(tab: string): string {
        return `/upload/document-upload?tab=${tab}`
    }
}

// Type for all valid navigation paths - ONLY enums and RouteBuilder calls allowed
export type ValidNavigationPath =
    | NavigationRoutes
    | ReturnType<
          | typeof RouteBuilders.intakeSurvey
          | typeof RouteBuilders.managerIntakeSurvey
          | typeof RouteBuilders.uploadDetails
          | typeof RouteBuilders.teamMemberDetail
          | typeof RouteBuilders.uploadHrisWithTab
          | typeof RouteBuilders.uploadPerformanceWithTab
          | typeof RouteBuilders.uploadDocumentWithTab
      >

// Helper function to validate if a path is a valid navigation route
export function isValidNavigationRoute(path: string): boolean {
    // Check if it's a static route
    if (Object.values(NavigationRoutes).includes(path as NavigationRoutes)) {
        return true
    }

    // Check if it matches dynamic route patterns
    const dynamicPatterns = [
        /^\/intake-survey\/[^/]+(\?step=\w+)?$/, // REST-compliant intake survey pattern
        /^\/intake(\?step=\w+)?$/, // Manager intake survey pattern
        /^\/upload\/[^/]+(\?tab=\w+)?$/, // Simplified upload details pattern
        /^\/upload\/(hris|performance|document)-upload(\?tab=\w+)?$/,
        /^\/team\/[^/]+$/,
    ]

    return dynamicPatterns.some((pattern) => pattern.test(path))
}

// Navigation context for different user roles
export const RoleBasedNavigation = {
    HR: {
        uploadMain: NavigationRoutes.UPLOAD, // Always go back to unified upload page
        uploadWithTab: (tab: string) => RouteBuilders.uploadHrisWithTab(tab),
        uploadDetails: (jobId: string, tab?: string) => RouteBuilders.uploadDetails(jobId, tab),
    },
    MANAGER: {
        uploadMain: NavigationRoutes.UPLOAD, // Always go back to unified upload page
        uploadWithTab: (tab: string) => RouteBuilders.uploadPerformanceWithTab(tab),
        uploadDetails: (jobId: string, tab?: string) => RouteBuilders.uploadDetails(jobId, tab),
    },
    EMPLOYEE: {
        uploadMain: NavigationRoutes.UPLOAD, // Always go back to unified upload page
        uploadWithTab: (tab: string) => RouteBuilders.uploadDocumentWithTab(tab),
        uploadDetails: (jobId: string, tab?: string) => RouteBuilders.uploadDetails(jobId, tab),
    },
} as const

export type UserRole = keyof typeof RoleBasedNavigation
